import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, FileText, Loader2, Printer, RotateCw, Trash2, Upload, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/dialog";
import { getErrorMessage } from "../../lib/query/errors";
import { cn } from "../../lib/utils";
import { documentsAndMessagesKeys } from "./api/documentsAndMessagesKeys";
import { pollForOptimisticDocument } from "./api/optimisticDocumentPolling";
import { uploadProofFile } from "./api/uploadProof";

/** Fax destination shown in the "Send Proofs via Fax" option. */
const FAX_NUMBER = "(866) 214-6093";

/**
 * File types the proof uploader accepts — PDF, JPG/JPEG, PNG (mirrors
 * proof-portal-ui's dealer-portal uploader). Drives the browse dialog's filter;
 * drag-and-drop is guarded separately in `addFiles` since the `accept`
 * attribute doesn't apply to dropped files.
 */
const ACCEPTED_FILE_TYPES = ".pdf,.jpg,.jpeg,.png";

/** Same allow-list as sets, used to reject disallowed files on every path. */
const ACCEPTED_EXTENSIONS = new Set([".pdf", ".jpg", ".jpeg", ".png"]);
const ACCEPTED_MIME_TYPES = new Set(["application/pdf", "image/jpeg", "image/png"]);

/**
 * Whether a file is an accepted proof type. Matches on the browser-reported
 * MIME type when present, otherwise falls back to the filename extension (the
 * same extension-based check the dealer-portal relies on).
 * @param {File} file - The candidate file.
 * @returns {boolean} True when the file is a PDF, JPG/JPEG, or PNG.
 */
function isAcceptedFile(file: File): boolean {
  if (ACCEPTED_MIME_TYPES.has(file.type)) {
    return true;
  }
  const dotIndex = file.name.lastIndexOf(".");
  const extension = dotIndex === -1 ? "" : file.name.slice(dotIndex).toLowerCase();
  return ACCEPTED_EXTENSIONS.has(extension);
}

/** Upload lifecycle for a single staged file. */
type UploadStatus = "pending" | "uploading" | "success" | "error";

/** A file the analyst has staged for upload. */
interface StagedFile {
  id: string;
  /** The underlying file, kept so it can be PUT to S3 on submit. */
  file: File;
  name: string;
  size: number;
  type: string;
  /** Object URL for image files, used for the thumbnail + preview. Revoked on removal/unmount. */
  preview?: string;
  status: UploadStatus;
  /** Human-readable failure reason, present only when `status === "error"`. */
  error?: string;
}

interface SendProofsModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Credit application id; fed straight through as the upload `appId`. */
  appId: string;
  /** Dealer id from the deal summary, tagged onto each upload when available. */
  dealerId?: string;
  readOnly?: boolean;
}

/**
 * Formats a byte count as a human-readable size (B / KB / MB).
 * @param {number} bytes - The file size in bytes.
 * @returns {string} The formatted size, e.g. "1.2 MB".
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// "Send Proofs" modal for the Docs & Msgs panel. Lets an analyst stage proof
// files (drag-and-drop or browse), preview them, and submit. On submit each
// file is uploaded to S3 via a BFF-issued presigned URL (see api/uploadProof);
// uploads run concurrently with per-file status + retry, and the modal closes
// (refreshing the docs list) only once every file has uploaded.
function SendProofsModal({ open, onOpenChange, appId, dealerId, readOnly = false }: SendProofsModalProperties) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [previewFile, setPreviewFile] = useState<StagedFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Message shown when one or more added files were rejected for their type. */
  const [rejectedError, setRejectedError] = useState<string>();
  const fileInputReference = useRef<HTMLInputElement>(null);

  // Revoke any outstanding object URLs when the modal unmounts so image
  // previews don't leak.
  useEffect(() => {
    return () => {
      setStagedFiles((current) => {
        for (const file of current) {
          if (file.preview) {
            URL.revokeObjectURL(file.preview);
          }
        }
        return current;
      });
    };
  }, []);

  // Closing the modal (Cancel, header ✕, overlay, or a completed submit)
  // drops the current selection so reopening always starts fresh.
  useEffect(() => {
    if (open) {
      return;
    }
    setStagedFiles((current) => {
      for (const file of current) {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      }
      return [];
    });
    setPreviewFile(null);
    setIsSubmitting(false);
    setRejectedError(undefined);
  }, [open]);

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (readOnly) {
        setRejectedError(t("workbench.actionNotAllowed"));
        return;
      }
      if (!files || files.length === 0) {
        return;
      }
      // FileList isn't iterable under the app's tsconfig lib, so spread doesn't
      // type-check here — Array.from is the correct conversion.
      // eslint-disable-next-line unicorn/prefer-spread
      const incoming = Array.from(files);
      // The browse dialog honours `accept`, but drag-and-drop does not — reject
      // anything that isn't a PDF/JPG/PNG on both paths.
      const accepted: File[] = [];
      const rejected: File[] = [];
      for (const file of incoming) {
        (isAcceptedFile(file) ? accepted : rejected).push(file);
      }

      setRejectedError(
        rejected.length === 0
          ? undefined
          : t("documents.sendProofsModal.rejectedFileType", {
              count: rejected.length,
              names: rejected.map((file) => file.name).join(", "),
            })
      );

      if (accepted.length === 0) {
        return;
      }
      const staged: StagedFile[] = accepted.map((file) => ({
        id: globalThis.crypto.randomUUID(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
        status: "pending",
      }));
      setStagedFiles((previous) => [...previous, ...staged]);
    },
    [readOnly, t]
  );

  const removeFile = useCallback((fileId: string) => {
    setStagedFiles((previous) => {
      const target = previous.find((file) => file.id === fileId);
      if (target?.preview) {
        URL.revokeObjectURL(target.preview);
      }
      return previous.filter((file) => file.id !== fileId);
    });
    setPreviewFile((current) => (current?.id === fileId ? null : current));
  }, []);

  const setFileStatus = useCallback((fileId: string, status: UploadStatus, error?: string) => {
    setStagedFiles((previous) => previous.map((file) => (file.id === fileId ? { ...file, status, error } : file)));
  }, []);

  // Uploads a single staged file, driving its status through
  // uploading → success/error. Returns whether the upload succeeded.
  const uploadFile = useCallback(
    async (staged: StagedFile): Promise<boolean> => {
      if (readOnly) {
        setRejectedError(t("workbench.actionNotAllowed"));
        return false;
      }
      setFileStatus(staged.id, "uploading");
      try {
        const proofFileId = await uploadProofFile({ appId, dealerId, file: staged.file });
        setFileStatus(staged.id, "success");
        // Start polling for document in background (don't await — let it run after modal closes)
        void pollForOptimisticDocument(queryClient, appId, proofFileId);
        return true;
      } catch (error) {
        setFileStatus(staged.id, "error", getErrorMessage(error));
        return false;
      }
    },
    [appId, queryClient, setFileStatus, dealerId, readOnly, t]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
      addFiles(event.dataTransfer.files);
    },
    [addFiles]
  );

  const handleSubmit = useCallback(async () => {
    if (readOnly) {
      setRejectedError(t("workbench.actionNotAllowed"));
      return;
    }
    // Upload everything not already uploaded (fresh files + prior failures).
    const pending = stagedFiles.filter((file) => file.status !== "success");
    if (pending.length === 0) {
      return;
    }
    setIsSubmitting(true);
    const results = await Promise.all(pending.map((file) => uploadFile(file)));

    // Only finish once every file has landed; failures stay staged for retry.
    const allSucceeded = results.every(Boolean);
    if (allSucceeded) {
      // Refresh the Docs & Msgs list with the just-uploaded proofs before
      // closing — await so the refetch is driven by the submit responses.
      await queryClient.invalidateQueries({ queryKey: documentsAndMessagesKeys.detail({ approvalId: appId }) });
    }
    setIsSubmitting(false);
    if (allSucceeded) {
      onOpenChange(false);
    }
  }, [stagedFiles, uploadFile, queryClient, appId, onOpenChange, readOnly, t]);

  const handleFaxClick = useCallback(() => {
    // TODO(OR-22536): point at the real fax cover-sheet asset once available.
    globalThis.open("/fax-cover-sheet.pdf", "_blank", "noopener,noreferrer");
  }, []);

  const hasFiles = stagedFiles.length > 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent showCloseButton={false} className="max-w-2xl gap-0 overflow-hidden p-0">
          {/* Header — navy bar with title + close. */}
          <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
            <DialogTitle className="text-primary-foreground text-sm font-semibold">
              {t("documents.sendProofsModal.title")}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t("documents.sendProofsModal.dropzone.instructions")}
            </DialogDescription>
            <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors">
              <X className="size-4.5" />
              <span className="sr-only">{t("documents.sendProofsModal.close")}</span>
            </DialogClose>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Fax option */}
            <button
              type="button"
              onClick={handleFaxClick}
              className="border-border hover:bg-accent flex w-full items-center gap-2 rounded-lg border p-2 text-left transition-colors"
            >
              <Printer className="text-primary size-4 shrink-0" />
              <span className="flex-1">
                <span className="text-primary block text-sm font-semibold">
                  {t("documents.sendProofsModal.fax.title")}
                </span>
                <span className="text-muted-foreground mt-0.5 block text-xs">
                  {t("documents.sendProofsModal.fax.description", { faxNumber: FAX_NUMBER })}
                </span>
              </span>
            </button>

            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="border-border w-full border-t" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background text-muted-foreground px-3 text-sm">
                  {t("documents.sendProofsModal.or")}
                </span>
              </div>
            </div>

            {/* Upload dropzone */}
            <div
              className={cn(
                "rounded-lg border-2 border-dashed px-3 py-2 text-center transition-colors",
                isDragging ? "border-primary bg-primary/5" : "border-border bg-muted"
              )}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <Upload className="text-primary/60 mx-auto mb-1 size-5" />
              <h3 className="text-primary text-xs font-semibold">{t("documents.sendProofsModal.dropzone.title")}</h3>
              <p className="text-muted-foreground text-xs">{t("documents.sendProofsModal.dropzone.instructions")}</p>
              <p className="text-muted-foreground mb-1.5 text-xs">{t("documents.sendProofsModal.dropzone.accepted")}</p>
              <Button type="button" size="xs" disabled={readOnly} onClick={() => fileInputReference.current?.click()}>
                {t("documents.sendProofsModal.dropzone.selectFiles")}
              </Button>
              <input
                ref={fileInputReference}
                type="file"
                disabled={readOnly}
                multiple
                accept={ACCEPTED_FILE_TYPES}
                aria-label={t("documents.sendProofsModal.dropzone.inputLabel")}
                className="hidden"
                onChange={(event) => addFiles(event.target.files)}
              />
            </div>

            {/* Rejected-type notice */}
            {rejectedError && (
              <p role="alert" className="text-destructive mt-2 text-xs">
                {rejectedError}
              </p>
            )}

            {/* Staged files */}
            {hasFiles && (
              <div className="mt-2">
                <h4 className="text-primary mb-1 text-xs font-semibold">
                  {t("documents.sendProofsModal.uploadedFiles")}
                </h4>
                <ul className="flex flex-col pl-3">
                  {stagedFiles.map((file) => (
                    <li key={file.id} className="flex items-center gap-2 py-0.5">
                      {/* Name — opens the preview. Shows a status line only while
                          uploading or after a failure. */}
                      <button
                        type="button"
                        onClick={() => setPreviewFile(file)}
                        aria-label={t("documents.sendProofsModal.fileThumbnail", { name: file.name })}
                        className="flex min-w-0 flex-1 items-baseline gap-2 text-left"
                      >
                        <span className="text-primary truncate text-xs font-medium">{file.name}</span>
                        {(file.status === "uploading" || file.status === "error") && (
                          <span
                            className={cn(
                              "text-2xs shrink-0",
                              file.status === "error" ? "text-destructive" : "text-muted-foreground"
                            )}
                          >
                            {file.status === "error"
                              ? (file.error ?? t("documents.sendProofsModal.status.failed"))
                              : t("documents.sendProofsModal.status.uploading")}
                          </span>
                        )}
                      </button>

                      {/* Status affordance. */}
                      {file.status === "uploading" && (
                        <Loader2
                          className="text-primary size-3.5 shrink-0 animate-spin"
                          aria-label={t("documents.sendProofsModal.status.uploading")}
                        />
                      )}
                      {file.status === "success" && (
                        <CheckCircle2
                          className="size-3.5 shrink-0 text-green-600"
                          aria-label={t("documents.sendProofsModal.status.uploaded")}
                        />
                      )}
                      {file.status === "error" && (
                        <>
                          <AlertCircle className="text-destructive size-3.5 shrink-0" aria-hidden="true" />
                          <button
                            type="button"
                            onClick={() => void uploadFile(file)}
                            disabled={readOnly}
                            aria-label={t("documents.sendProofsModal.status.retryFile", { name: file.name })}
                            className="text-primary hover:text-primary/80 shrink-0 transition-colors"
                          >
                            <RotateCw className="size-3.5" />
                          </button>
                        </>
                      )}

                      {/* Remove — disabled mid-upload. */}
                      <button
                        type="button"
                        onClick={() => removeFile(file.id)}
                        disabled={file.status === "uploading"}
                        aria-label={t("documents.sendProofsModal.status.removeFile", { name: file.name })}
                        className="text-muted-foreground hover:text-destructive shrink-0 transition-colors disabled:opacity-40"
                      >
                        <X className="size-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-muted flex items-center justify-end gap-2 border-t px-4 py-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="sm">
                {t("documents.sendProofsModal.cancel")}
              </Button>
            </DialogClose>
            <Button
              type="button"
              size="sm"
              disabled={readOnly || !hasFiles || isSubmitting}
              onClick={() => void handleSubmit()}
            >
              {isSubmitting ? t("documents.sendProofsModal.submitting") : t("documents.sendProofsModal.submit")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* File preview */}
      <Dialog open={previewFile !== null} onOpenChange={(next) => !next && setPreviewFile(null)}>
        <DialogContent
          showCloseButton
          aria-describedby={undefined}
          className="max-h-[90vh] max-w-2xl gap-0 overflow-hidden p-0"
        >
          <DialogTitle className="sr-only">{previewFile?.name ?? ""}</DialogTitle>
          <div className="bg-muted flex-1 overflow-auto p-6">
            {previewFile?.preview ? (
              <img src={previewFile.preview} alt={previewFile.name} className="mx-auto max-w-full" />
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="text-primary/30 size-16" />
                <p className="text-muted-foreground mt-4 text-base">
                  {t("documents.sendProofsModal.preview.unavailable")}
                </p>
                <p className="text-muted-foreground mt-2 text-xs">{previewFile?.name}</p>
              </div>
            )}
          </div>
          <div className="bg-muted flex items-center justify-between border-t px-4 py-2">
            <div className="min-w-0">
              <p className="text-primary truncate text-sm font-semibold">{previewFile?.name}</p>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {previewFile ? formatFileSize(previewFile.size) : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => previewFile && removeFile(previewFile.id)}
              >
                <Trash2 className="size-3" />
                {t("documents.sendProofsModal.preview.remove")}
              </Button>
              <Button type="button" size="sm" onClick={() => setPreviewFile(null)}>
                {t("documents.sendProofsModal.preview.looksGood")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SendProofsModal;
